/**
 * Visumo（hacobune）のタイル内キャプションを、カンプの形に整形する。
 *
 * Visumo は Instagram のキャプションを丸ごと1つの <span> に入れて出力するため、
 * CSS では一部だけを消せない。そこで描画後に必要な行だけを抜き出して差し替える。
 *
 * 抜き出す2行（Figma 237:3648 / 237:3649）:
 *   タイトル … 【】で囲まれた行（例「【新作ワンダーシェイプ】」）
 *   身長     … 「STAFF:176cm」「model：176㎝」等から数字＋cm を取り出す
 *
 * どちらも見つからない場合は元のキャプションに触れない（消し過ぎ防止）。
 * 商品詳細ページ（Staff Coordinate）のみを対象にする。
 */
(() => {
  'use strict';

  const ROOT = '.c-designer';
  const ITEM = '.hacobune-mix-list-item-description';
  const DONE = 'data-vsm-caption-formatted';
  const SLIDER_DONE = 'data-vsm-slider-ready';

  const CLASS = {
    title: 'c-vsm-caption__title',
    height: 'c-vsm-caption__height',
  };

  /** 【】で囲まれた最初の行を返す */
  const pickTitle = (lines) => {
    const hit = lines.find((l) => /【.+】/.test(l));
    if (!hit) return '';
    const m = hit.match(/【(.+?)】/);
    return m ? `【${m[1]}】` : hit;
  };

  /** 「STAFF:176cm」「model：176㎝」等から「176cm」を取り出す */
  const pickHeight = (lines) => {
    for (const line of lines) {
      const m = line.match(/(\d{2,3})\s*(?:cm|㎝)/i);
      if (m) return `${m[1]}cm`;
    }
    return '';
  };

  const format = (el) => {
    if (el.hasAttribute(DONE)) return;

    const raw = el.textContent || '';
    const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
    const title = pickTitle(lines);
    const height = pickHeight(lines);

    // どちらも取れないキャプションは、判断材料がないので元のまま残す
    if (!title && !height) {
      el.setAttribute(DONE, 'skip');
      return;
    }

    el.textContent = '';
    if (title) {
      const p = document.createElement('p');
      p.className = CLASS.title;
      p.textContent = title;
      el.appendChild(p);
    }
    if (height) {
      const p = document.createElement('p');
      p.className = CLASS.height;
      p.textContent = height;
      el.appendChild(p);
    }
    el.setAttribute(DONE, '1');
  };

  const run = () => {
    document.querySelectorAll(`${ROOT} ${ITEM}`).forEach(format);
    document.querySelectorAll(`${ROOT} .hacobune-goods .hacobune-grid-container`).forEach(enhanceSlider);
  };

  /** 商品逆引き対応の tile を、PCでも横送りできるUIにする */
  function enhanceSlider(container) {
    if (container.hasAttribute(SLIDER_DONE)) return;

    const row = container.querySelector('.hacobune-grid-row');
    if (!row) return;

    const createButton = (direction, label) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `c-vsm-slider-button c-vsm-slider-button--${direction}`;
      button.setAttribute('aria-label', label);
      return button;
    };

    const prev = createButton('prev', '前のコーディネートを表示');
    const next = createButton('next', '次のコーディネートを表示');

    const update = () => {
      const max = Math.max(0, row.scrollWidth - row.clientWidth);
      const hasOverflow = max > 2;
      prev.hidden = !hasOverflow || row.scrollLeft <= 2;
      next.hidden = !hasOverflow || row.scrollLeft >= max - 2;
    };

    const move = (direction) => {
      const first = row.querySelector('.hacobune-grid-col');
      const gap = parseFloat(getComputedStyle(row).gap) || 0;
      const distance = first ? first.getBoundingClientRect().width + gap : row.clientWidth;
      row.scrollBy({ left: direction * distance, behavior: 'smooth' });
    };

    prev.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    container.append(prev, next);
    container.classList.add('c-vsm-slider-ready');
    container.setAttribute(SLIDER_DONE, '1');
    requestAnimationFrame(update);
  }

  const start = () => {
    run();
    // Visumo は非同期描画かつ、スライド送りなどで再描画されるため監視し続ける
    const root = document.querySelector(ROOT);
    if (!root) return;
    new MutationObserver(run).observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
